import { useRef, useEffect } from "react";
import { getMediaCentralUrl } from "../services/media-service";
import type { ClimatePledgeFriendlyFeatures, AppSettings } from "../types";
import { ResponsiveSheet } from "./ResponsiveSheet";
import { ChevronIcon } from "./ui";
import { BADGE_SIZING } from "../../../../constants";
import {
  useComponentState,
  useSelectionHandlers,
  useFocusManagement,
} from "../hooks";
import { useMetricEmitter } from "../contexts/MetricEmitterContext";

interface SustainabilityBadgeProps {
  cpfData: ClimatePledgeFriendlyFeatures;
  settings: AppSettings;
}

export const SustainabilityBadge = ({
  cpfData,
  settings,
}: SustainabilityBadgeProps) => {
  const badgeRef = useRef<HTMLSpanElement>(null);
  const focusManagement = useFocusManagement();
  const { sheet } = useComponentState();
  const { createBadgeHandlers } = useSelectionHandlers();
  const metricEmitter = useMetricEmitter();

  // Track badge render latency when badge component mounts and is ready
  useEffect(() => {
    requestAnimationFrame(() => {
      metricEmitter.trackLatency("badge");
    });
  }, [metricEmitter]);

  const openSheetWithMetrics = () => {
    focusManagement.captureScrollPosition();

    // Track badge click metric
    metricEmitter.trackClick("bg");
    sheet.open();
  };

  const { handleBadgeClick, handleKeyDown } =
    createBadgeHandlers(openSheetWithMetrics);

  // Helper function to calculate sizes based on badge size setting
  const getBadgeSizes = () => {
    const badgeSize = settings["badge-size"] || "medium";
    const scaleFactor = BADGE_SIZING.scaleFactors[badgeSize] || 1.0;
    return {
      iconSize: Math.round(BADGE_SIZING.baseIconSize * scaleFactor),
      fontSize: Math.round(BADGE_SIZING.baseFontSize * scaleFactor),
      chevronWidth: Math.round(BADGE_SIZING.baseChevronWidth * scaleFactor),
      chevronHeight: Math.round(BADGE_SIZING.baseChevronHeight * scaleFactor),
    };
  };

  const { iconSize, fontSize, chevronWidth, chevronHeight } = getBadgeSizes();

  return (
    <>
      <div className="sustain-badge-container">
        <span
          ref={badgeRef}
          id="sustainability-badge-button"
          tabIndex={0}
          role="button"
          className={`sustain-badge sustain-badge--${settings["badge-size"]}`}
          onClick={handleBadgeClick}
          onKeyDown={handleKeyDown}
          aria-haspopup="dialog"
          style={{ fontSize: `${fontSize}px` }}
        >
          <img
            src={getMediaCentralUrl(
              cpfData.icon?.physicalId ?? "",
              cpfData.icon?.extension ?? "png",
            )}
            alt=""
            className="sustain-badge-icon"
            style={{ width: `${iconSize}px`, height: `${iconSize}px` }}
          />
          <span
            className="sustain-badge-text"
            style={{ fontSize: `${fontSize}px` }}
          >
            {cpfData.displayName}
            <ChevronIcon width={chevronWidth} height={chevronHeight} />
          </span>
        </span>
      </div>

      {/* Responsive Sheet - Automatically adapts to device type */}
      <ResponsiveSheet
        isOpen={sheet.isOpen}
        onClose={sheet.close}
        settings={settings}
        cpfData={cpfData}
        focusManagement={focusManagement}
        badgeRef={badgeRef}
        metricEmitter={metricEmitter}
      />
    </>
  );
};
